import * as BABYLON from "@babylonjs/core/Legacy/legacy";

import { config } from "../config.js";
import { Player } from "../player.js";
import { CoinMeshPool } from "./coin.js";
import { MeshPool } from "./meshPool.js";
import { IMeshPool, PathMeshType } from "./common.js";
import { FirePool, Fire } from "./fire.js";
import { GameScene } from "../scene.js";
import { BoxBlock, Vector3 } from "@babylonjs/core/Legacy/legacy";

const segmentSize = 1;
const log = console.log.bind(console);

export enum PathSegmentType {
    straight = 1,
    turnRight,
    turnLeft,
}

enum PathSegmentDirection {
    xPlus,
    xMinus,
    zPlus,
    zMinus,
}

export class PathSegmentOptions {
    coin: boolean;
    fire: boolean;

    constructor(coin?: boolean, fire?: boolean) {
        this.coin = coin;
        this.fire = fire;
    }
}

/*
基本的路块，断裂的路块，带阻碍的路块都是展示用
从不同的 meshPool 里面获取 mesh 放到 PathSegment 做显示

PathSegment 是不同模样的路块共用的逻辑类
这里面包含了路块的拐弯 速度 方向等等
*/
export class PathSegment {
    type: PathSegmentType;
    meshType: PathMeshType;

    //
    gameScene: GameScene;
    bbScene: BABYLON.Scene;
    camera: BABYLON.Camera;
    player: Player;
    meshPool: MeshPool;

    // x y z
    position: BABYLON.Vector2;
    prev: PathSegment;
    next: PathSegment;
    size: number;
    pathMesh: BABYLON.Mesh;
    coinMesh: BABYLON.Mesh;
    fire: Fire;
    collideEndBox: BABYLON.Mesh = null;

    // 初始速度
    speed: BABYLON.Vector3;
    // 拐弯后的速度
    speedTurn: BABYLON.Vector3;
    turnSuccess: boolean;

    // 初始方向
    direction: PathSegmentDirection;
    directionTurn: PathSegmentDirection;
    directionRaw: string;

    // 拐弯动画
    cameraAnimation: BABYLON.Animation;
    playerAnimation: BABYLON.Animation;
    diffRadius: number;
    frameRate: number;
    animationDone: boolean;

    // 左移 右移 距离
    moveLeftVec: BABYLON.Vector3;
    moveRightVec: BABYLON.Vector3;

    options: PathSegmentOptions;

    constructor(type: PathSegmentType, x: number, z: number, direction: string, meshType: PathMeshType, options: PathSegmentOptions) {
        this.type = type;
        this.meshType = meshType;
        this.position = new BABYLON.Vector2(x, z);
        this.next = null;
        this.prev = null;
        this.size = segmentSize;
        this.pathMesh = null;
        this.coinMesh = null;
        this.fire = null;
        this.options = options;

        const speedByDir = function (dir: string): BABYLON.Vector3 {
            if (dir === "+z") {
                return new BABYLON.Vector3(0, 0, speed);
            } else if (dir === "+x") {
                return new BABYLON.Vector3(speed, 0, 0);
            } else if (dir === "-x") {
                return new BABYLON.Vector3(-speed, 0, 0);
            } else if (dir === "-z") {
                return new BABYLON.Vector3(0, 0, -speed);
            }
            return null;
        };

        const directionByDir = function (dir: string): PathSegmentDirection {
            if (dir === "+z") {
                return PathSegmentDirection.zPlus;
            } else if (dir === "-z") {
                return PathSegmentDirection.zMinus;
            } else if (dir === "+x") {
                return PathSegmentDirection.xPlus;
            } else if (dir === "-x") {
                return PathSegmentDirection.xMinus;
            }
            return null;
        };

        let speed = config.speed;
        // 将 direction 用 ; 分割
        let dirs = direction.split(";");
        let dir0 = dirs[0];

        this.speed = speedByDir(dir0);
        this.direction = directionByDir(dir0);
        this.directionRaw = direction;

        if (this.isTurn()) {
            this.speedTurn = speedByDir(dirs[1]);
            this.directionTurn = directionByDir(dirs[1]);
        }
        this.turnSuccess = false;

        this.setupMoveDistance();
        this.setupAnimation();
    }

    setupMoveDistance() {
        let dist = 0.25;
        if (this.direction === PathSegmentDirection.xMinus) {
            this.moveLeftVec = new BABYLON.Vector3(0, 0, -dist);
            this.moveRightVec = new BABYLON.Vector3(0, 0, dist);
        } else if (this.direction === PathSegmentDirection.xPlus) {
            this.moveLeftVec = new BABYLON.Vector3(0, 0, dist);
            this.moveRightVec = new BABYLON.Vector3(0, 0, -dist);
        } else if (this.direction === PathSegmentDirection.zPlus) {
            this.moveLeftVec = new BABYLON.Vector3(-dist, 0, 0);
            this.moveRightVec = new BABYLON.Vector3(dist, 0, 0);
        } else if (this.direction === PathSegmentDirection.zMinus) {
            this.moveLeftVec = new BABYLON.Vector3(dist, 0, 0);
            this.moveRightVec = new BABYLON.Vector3(-dist, 0, 0);
        }
    }

    setupAnimation() {
        if (this.type === PathSegmentType.straight) {
            return;
        }

        // 相机转向动画
        let frameRate = config.turnAnimationFramerate;
        this.frameRate = frameRate;
        let ani = new BABYLON.Animation(
            "camera_rotate",
            "alpha",
            frameRate,
            BABYLON.Animation.ANIMATIONTYPE_FLOAT,
            BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT,
        );

        // 左拐 右拐 各 4 个角度，所以有 8 种情况
        let cases = new Map();
        cases[PathSegmentType.turnLeft] = {
            "-x;-z": [0, Math.PI / 2],
            "-z;+x": [Math.PI / 2, Math.PI],
            "+x;+z": [Math.PI, Math.PI + Math.PI / 2],
            "+z;-x": [Math.PI + Math.PI / 2, Math.PI * 2],
        };
        cases[PathSegmentType.turnRight] = {
            "+z;+x": [-Math.PI / 2, -Math.PI],
            "+x;-z": [-Math.PI, -Math.PI - Math.PI / 2],
            "-z;-x": [-Math.PI - Math.PI / 2, -2 * Math.PI],
            "-x;+z": [0, -Math.PI / 2],
        };

        // log('cases', cases, cases[this.type], cases[this.type][this.directionRaw], this.directionRaw)
        let keys = [];
        keys.push({
            frame: 0,
            value: cases[this.type][this.directionRaw][0],
        });
        keys.push({
            frame: frameRate,
            value: cases[this.type][this.directionRaw][1],
        });
        ani.setKeys(keys);
        this.cameraAnimation = ani;

        if (this.type === PathSegmentType.turnLeft) {
            this.diffRadius = -Math.PI / 2;
        } else if (this.type === PathSegmentType.turnRight) {
            this.diffRadius = Math.PI / 2;
        }

        this.animationDone = false;
    }

    playAnimation(scene: BABYLON.Scene, camera: BABYLON.Camera, player: Player) {
        if (this.animationDone || player.getRunSpeed() === 0) {
            return;
        }

        let self = this;

        // self.speed = new BABYLON.Vector3(0, 0, 0);
        // 让人物先不移动
        player.setRunSpeed(0);

        // 相机动画
        camera.animations.push(self.cameraAnimation);
        scene.beginAnimation(camera, 0, self.frameRate, false, config.turnAnimationSpeedRatio, () => {
            // console.log("after");
            // self.speed = self.speedTurn

            self.animationDone = true;

            // 将 player 转体
            player.rotate(new BABYLON.Vector3(0, 1, 0), self.diffRadius);

            // 重置相机的动画
            camera.animations.pop();

            // 重启人物的移动
            player.setRunSpeed(config.runSpeed);
        });
    }

    // 判断是否包含玩家当前位置
    contain(playerPos: BABYLON.Vector2): boolean {
        let x = playerPos.x;
        let z = playerPos.y;
        return (
            this.position.x - this.size / 2 <= x &&
            x <= this.position.x + this.size / 2 &&
            this.position.y - this.size / 2 <= z &&
            z <= this.position.y + this.size / 2
        );
    }

    setup(player: Player, meshPool: MeshPool, gameScene: GameScene, camera: BABYLON.Camera): void {
        this.player = player;
        this.meshPool = meshPool;
        this.gameScene = gameScene;
        this.bbScene = gameScene.bbScene;
        this.camera = camera;
        this.setupMesh(meshPool);
    }

    setupMesh(meshPool: MeshPool): void {
        let self = this;

        // 围绕物体坐标系中心 旋转
        const rotateMesh = function (mesh: BABYLON.Mesh, direction: PathSegmentDirection) {
            // 根据道路的方向调整 mesh 的转向和相对位置
            let axis = new BABYLON.Vector3(0, 1, 0);
            let space = BABYLON.Space.WORLD;
            let amount = 0;
            if (direction === PathSegmentDirection.xPlus) {
                amount = Math.PI / 2;
            } else if (direction === PathSegmentDirection.xMinus) {
                amount = -Math.PI / 2;
            } else if (direction === PathSegmentDirection.zMinus) {
                amount = Math.PI;
            }
            // amount = -Math.PI / 2;
            log("amount_1", direction, amount, mesh.rotationQuaternion);
            mesh.rotate(axis, amount, space);
            log("amount_2", direction, amount, mesh.rotationQuaternion);
        };

        // 围绕路方块中心做 旋转
        const meshPositionRotate = function (mesh: BABYLON.Mesh, direction: PathSegmentDirection) {
            let angle = 0;
            if (direction === PathSegmentDirection.xPlus) {
                angle = Math.PI / 2;
            } else if (direction === PathSegmentDirection.xMinus) {
                angle = -Math.PI / 2;
            } else if (direction === PathSegmentDirection.zMinus) {
                angle = Math.PI;
            }
            // angle = -Math.PI / 2;

            const rotQuart = BABYLON.Quaternion.RotationAxis(BABYLON.Axis.Y, angle);
            mesh.position.rotateByQuaternionAroundPointToRef(
                rotQuart,
                new Vector3(self.position.x, mesh.position.y, self.position.y),
                mesh.position,
            );
        };

        // 路面方块
        let mesh = meshPool.getMeshPool(this.meshType).getMesh();
        if (mesh === undefined || mesh === null) {
            log("meshPool empty", mesh);
            return;
        }
        this.pathMesh = mesh;
        mesh.position.x = this.position.x;
        mesh.position.z = this.position.y;
        // 转向
        rotateMesh(mesh, this.direction);

        // 金币
        if (this.options.coin) {
            log("---coin");
            mesh = meshPool.getMeshPool(PathMeshType.coin).getMesh();
            if (mesh === undefined || mesh === null) {
                log("coinPool empty", mesh);
                return;
            }
            this.coinMesh = mesh;
            let pos = this.pathMesh.position;
            this.coinMesh.position.set(pos.x, pos.y + 0.4, pos.z);
            // 转向
            rotateMesh(mesh, this.direction);
        }

        // 火
        if (this.options.fire) {
            let firePool = meshPool.getFirePool();
            let fire = firePool.getFire();
            fire.setPosition(this.position.x, this.position.y);
            this.fire = fire;

            if (this.direction === PathSegmentDirection.xPlus || this.direction === PathSegmentDirection.xMinus) {
                fire.rotate(Math.PI / 2);
            }
        }

        // 路尽头的碰撞方块
        if (this.isTurn()) {
            log("---collideEndBoxPool");
            let box = meshPool.collideEndBoxPool.getMesh();
            // 位置
            box.position.x = this.position.x;
            box.position.z = this.position.y + config.pathSegmentSize / 2;

            // 围绕路中心做旋转
            meshPositionRotate(box, this.direction);
            rotateMesh(box, this.direction);

            this.collideEndBox = box;
        }
    }

    // 需要 rotate back 的原因是直接设置 rotation 不再管用
    private _rotateMeshBack = function (mesh: BABYLON.Mesh, direction: PathSegmentDirection) {
        // 根据道路的方向调整 mesh 的转向和相对位置
        let axis = new BABYLON.Vector3(0, 1, 0);
        let space = BABYLON.Space.WORLD;
        let amount = 0;
        if (direction === PathSegmentDirection.xPlus) {
            amount = Math.PI / 2;
        } else if (direction === PathSegmentDirection.xMinus) {
            amount = -Math.PI / 2;
        } else if (direction === PathSegmentDirection.zMinus) {
            amount = Math.PI;
        }
        mesh.rotate(axis, -amount, space);
    };

    releaseCoinMesh(coinPool: CoinMeshPool) {
        if (this.coinMesh === null) {
            return;
        }

        this._rotateMeshBack(this.coinMesh, this.direction);
        coinPool.returnMesh(this.coinMesh);
        this.coinMesh = null;
    }

    releaseMesh(meshPool: MeshPool): void {
        if (this.pathMesh === null) {
            return;
        }

        // 释放地板 mesh
        this._rotateMeshBack(this.pathMesh, this.direction);
        meshPool.getMeshPool(this.meshType).returnMesh(this.pathMesh);
        this.pathMesh = null;

        // 释放金币 mesh
        this.releaseCoinMesh(meshPool.coinPool);

        // 释放 fire
        if (this.fire !== null) {
            if (this.direction === PathSegmentDirection.xPlus || this.direction === PathSegmentDirection.xMinus) {
                this.fire.rotate(-Math.PI / 2);
            }
            meshPool.firePool.returnFire(this.fire);
        }

        // 释放碰撞的墙
        if (this.isTurn() && this.collideEndBox !== null) {
            this._rotateMeshBack(this.collideEndBox, this.direction);
            meshPool.collideEndBoxPool.returnMesh(this.collideEndBox);
        }
    }

    arriveCenter(pos: BABYLON.Vector2): boolean {
        let x = pos.x;
        let z = pos.y;
        let speed = this.speed.length();
        if (this.direction === PathSegmentDirection.zPlus || this.direction === PathSegmentDirection.zMinus) {
            return this.position.y - speed <= z && z <= this.position.y + speed;
        } else if (this.direction === PathSegmentDirection.xPlus || this.direction === PathSegmentDirection.xMinus) {
            return this.position.x - speed <= x && x <= this.position.x + speed;
        }
    }

    isTurn(): boolean {
        let cur = this;
        return cur.type === PathSegmentType.turnLeft || cur.type === PathSegmentType.turnRight;
    }

    markTurnSuccess() {
        // log('拐弯成功')
        this.turnSuccess = true;
    }

    // 选择在 pat segment 里面做移动的原因是 可以后续判断有没有移出范围
    moveLeft(player: Player) {
        let dist = 0.25;
        let x = player.pos2D().x;
        let y = player.pos2D().y;

        // 需要保证左移不会越过墙
        if (this.direction === PathSegmentDirection.xMinus && y - dist > this.position.y - config.pathSegmentSize / 2) {
            player.moveLeftToZ(y - dist);
        } else if (this.direction === PathSegmentDirection.xPlus && y + dist < this.position.y + config.pathSegmentSize / 2) {
            player.moveLeftToZ(y + dist);
        } else if (this.direction === PathSegmentDirection.zPlus && x - dist > this.position.x - config.pathSegmentSize / 2) {
            player.moveLeftToX(x - dist);
        } else if (this.direction === PathSegmentDirection.zMinus && x + dist < this.position.x + config.pathSegmentSize / 2) {
            player.moveLeftToX(x + dist);
        }
    }

    moveRight(player: Player) {
        let dist = 0.25;
        let x = player.pos2D().x;
        let y = player.pos2D().y;

        // 需要保证右移不会越过墙
        if (this.direction === PathSegmentDirection.xMinus && y + dist < this.position.y + config.pathSegmentSize / 2) {
            player.moveRightToZ(y + dist);
        } else if (this.direction === PathSegmentDirection.xPlus && y - dist > this.position.y - config.pathSegmentSize / 2) {
            player.moveRightToZ(y - dist);
        } else if (this.direction === PathSegmentDirection.zPlus && x + dist < this.position.x + config.pathSegmentSize / 2) {
            player.moveRightToX(x + dist);
        } else if (this.direction === PathSegmentDirection.zMinus && x - dist > this.position.x - config.pathSegmentSize / 2) {
            player.moveRightToX(x - dist);
        }
    }

    checkCoinIntersectPlayer(player: Player, coinPool: CoinMeshPool) {
        if (this.coinMesh === null) {
            return;
        }

        if (player.intersectsMesh(this.coinMesh)) {
            // 金币回收
            this.releaseCoinMesh(coinPool);

            // 粒子
            this.gameScene.scene2d.addCoinParticle();
        }
    }

    checkCollideEndBoxIntersectPlayer() {
        if (this.isTurn() === false) {
            return;
        }

        let player = this.player;
        let pool = this.meshPool.collideEndBoxPool;

        if (player.intersectsMesh(this.collideEndBox)) {
            pool.returnMesh(this.collideEndBox);

            log("撞到了墙");
            this.gameScene.isEnd = true;

            // gui 显示结束文字
            this.gameScene.scene2d.end();
        }
    }

    update() {
        // 如果是拐弯的路段 判断是否路段的中心
        if (this.isTurn() && this.turnSuccess) {
            let pos = this.player.pos2D();
            if (this.arriveCenter(pos) && this.animationDone === false) {
                // log('到达拐弯中点')
                // 到达中点的判断不一定精准 所以需要精准设置
                if (this.direction === PathSegmentDirection.zPlus || this.direction === PathSegmentDirection.zMinus) {
                    this.player.setPosZ(this.position.y);
                } else if (this.direction === PathSegmentDirection.xPlus || this.direction === PathSegmentDirection.xMinus) {
                    this.player.setPosX(this.position.x);
                }
                // 播放动画
                this.playAnimation(this.bbScene, this.camera, this.player);
            }
        }

        // 雾的颜色
        if (this.fire) {
            this.fire.update();
        }
    }

    checkCollision() {
        // 判断和金币的碰撞
        this.checkCoinIntersectPlayer(this.player, this.meshPool.coinPool);
        // 判断撞到墙
        this.checkCollideEndBoxIntersectPlayer();
    }
}
